import React from 'react';
import { Button, Card, Select, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { LoadingPolicyCatalog, LoadingPolicyIssue, LoadingPolicyParamSpec } from '../graphql/loadingPolicyGraphql';
import { CheckFormItem, checkIndexOfPath, findSpec, newCheckKey, updateParam, visibleParams } from '../loadingPolicyForm';

type CheckListEditorProps = {
  catalog: LoadingPolicyCatalog;
  value: CheckFormItem[];
  onChange: (next: CheckFormItem[]) => void;
  /** 后端返回的错误，path 为 checks[i] 的定位到对应条目 */
  errors: LoadingPolicyIssue[];
};

/** 渠道 ID 等整数名单：输入后回车生成标签，非整数丢弃 */
const toLongList = (values: string[]): number[] =>
  values.map((v) => Number(String(v).trim())).filter((n) => Number.isInteger(n));

const ParamInput: React.FC<{
  param: LoadingPolicyParamSpec;
  value: unknown;
  onChange: (value: unknown) => void;
}> = ({ param, value, onChange }) => {
  const options = param.options.map((o) => ({ value: o.value, label: o.label }));
  if (param.type === 'ENUM') {
    return (
      <Select
        className="w-full"
        allowClear={!param.required}
        placeholder="请选择"
        options={options}
        value={(value as string) ?? undefined}
        onChange={(v) => onChange(v)}
      />
    );
  }
  if (param.type === 'ENUM_SET') {
    return (
      <Select
        className="w-full"
        mode="multiple"
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="请选择"
        options={options}
        value={(value as string[]) ?? []}
        onChange={(v) => onChange(v)}
      />
    );
  }
  return (
    <Select
      className="w-full"
      mode="tags"
      tokenSeparators={[',', '，', ' ']}
      placeholder="输入整数后回车"
      open={false}
      value={((value as number[]) ?? []).map(String)}
      onChange={(v: string[]) => onChange(toLongList(v))}
    />
  );
};

const CheckListEditor: React.FC<CheckListEditorProps> = ({ catalog, value, onChange, errors }) => {
  const errorsByIndex = new Map<number, string[]>();
  errors.forEach((e) => {
    const index = checkIndexOfPath(e.path);
    if (index !== null) errorsByIndex.set(index, [...(errorsByIndex.get(index) ?? []), e.message]);
  });

  const usedCodes = new Set(value.map((v) => v.code));
  const addOptions = catalog.checks.map((spec) => ({
    value: spec.code,
    label: `${spec.label}（${spec.code}）`,
    // 不可重复的校验已配置时不能再加；可重复的（按属性 / 类型区分）允许再加，重复由后端校验拦截
    disabled: usedCodes.has(spec.code) && !spec.repeatableBy,
  }));

  const update = (index: number, patch: Partial<CheckFormItem>) =>
    onChange(value.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));

  const add = (code: string) => onChange([...value, { key: newCheckKey(), code, params: {} }]);

  const ordered = value
    .map((item, index) => ({ item, index, order: findSpec(catalog, item.code)?.order ?? 999 }))
    .sort((a, b) => a.order - b.order);

  return (
    <div className="flex flex-col gap-3">
      {ordered.map(({ item, index }) => {
        const spec = findSpec(catalog, item.code);
        const itemErrors = errorsByIndex.get(index) ?? [];
        const removable = spec ? !(spec.mandatory && value.filter((v) => v.code === item.code).length <= 1) : true;
        return (
          <Card
            key={item.key}
            size="small"
            className={itemErrors.length ? 'border-red-400' : undefined}
            title={
              <span>
                {spec?.label ?? item.code}
                <Typography.Text type="secondary" className="ml-2 text-xs">
                  {item.code}
                </Typography.Text>
                {spec?.mandatory && (
                  <Tag color="blue" className="ml-2">
                    必配
                  </Tag>
                )}
              </span>
            }
            extra={
              <Tooltip title={removable ? '移除' : '必配项不能移除'}>
                <Button type="text" danger icon={<DeleteOutlined />} disabled={!removable} onClick={() => remove(index)} />
              </Tooltip>
            }
          >
            {spec ? (
              <div className="flex flex-col gap-2">
                <Typography.Text type="secondary" className="text-xs">
                  {spec.description}
                </Typography.Text>
                {visibleParams(spec, item.params).map((param) => (
                  <div key={`${param.name}-${param.label}`} className="flex items-center gap-2">
                    <span className="w-40 shrink-0 text-right">
                      {param.required && <span className="text-red-500">* </span>}
                      {param.label}
                    </span>
                    <div className="flex-1">
                      <ParamInput
                        param={param}
                        value={item.params[param.name]}
                        onChange={(v) => update(index, { params: updateParam(spec, item.params, param.name, v) })}
                      />
                      {param.description && (
                        <Typography.Text type="secondary" className="text-xs">
                          {param.description}
                        </Typography.Text>
                      )}
                    </div>
                  </div>
                ))}
                {spec.allowedOnFail.length > 1 && (
                  <div className="flex items-center gap-2">
                    <span className="w-40 shrink-0 text-right">失败处理</span>
                    <Select
                      className="flex-1"
                      options={spec.allowedOnFail.map((o) => ({ value: o.value, label: o.label }))}
                      value={item.onFail ?? 'BLOCK'}
                      onChange={(v) => update(index, { onFail: v === 'BLOCK' ? undefined : v })}
                    />
                  </div>
                )}
              </div>
            ) : (
              <Typography.Text type="danger">未知校验项 {item.code}，请移除</Typography.Text>
            )}
            {itemErrors.map((msg) => (
              <div key={msg} className="mt-2 text-xs text-red-500">
                {msg}
              </div>
            ))}
          </Card>
        );
      })}
      <Select
        className="w-full"
        placeholder="+ 添加校验项"
        value={null as unknown as string}
        options={addOptions}
        onChange={(code: string) => add(code)}
      />
    </div>
  );
};

export default CheckListEditor;
